import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, addDoc, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function Dashboard() {
    const { currentUser, userProfile, logout } = useAuth();
    const navigate = useNavigate();
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHostRequest, setShowHostRequest] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [myHostRequests, setMyHostRequests] = useState([]);
    const [editingRequest, setEditingRequest] = useState(null);
    const [showCelebration, setShowCelebration] = useState(false);

    useEffect(() => {
        loadGames();
        loadMyHostRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadGames() {
        try {
            const gamesQuery = query(collection(db, 'games'));
            const gamesSnapshot = await getDocs(gamesQuery);

            const gamesData = gamesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setGames(gamesData);
            setLoading(false);
        } catch (err) {
            console.error('Error loading games:', err);
            setLoading(false);
        }
    }

    async function loadMyHostRequests() {
        try {
            const requestsQuery = query(
                collection(db, 'hostRequests'),
                where('userId', '==', currentUser.uid),
                where('status', '==', 'pending')
            );
            const requestsSnapshot = await getDocs(requestsQuery);

            const requestsData = requestsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setMyHostRequests(requestsData);
        } catch (err) {
            console.error('Error loading host requests:', err);
        }
    }

    async function handleDeleteHostRequest(requestId) {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הבקשה?')) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'hostRequests', requestId));
            await loadMyHostRequests();
        } catch (err) {
            console.error('Error deleting host request:', err);
            alert('שגיאה במחיקת הבקשה: ' + err.message);
        }
    }

    async function handleLogout() {
        try {
            await logout();
            navigate('/login');
        } catch (err) {
            console.error('Failed to log out', err);
        }
    }

    // Helper to check if a game is today
    const isGameToday = (game) => {
        if (!game.date) return false;
        const gameDate = new Date(game.date.seconds * 1000);
        const today = new Date();
        return gameDate.getDate() === today.getDate() &&
               gameDate.getMonth() === today.getMonth() &&
               gameDate.getFullYear() === today.getFullYear();
    };

    // Helper to check if a game is in the past
    const isGamePast = (game) => {
        if (!game.date) return false;
        const gameDate = new Date(game.date.seconds * 1000);
        // A game is past if it's before today (not including today)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const gameDateOnly = new Date(gameDate);
        gameDateOnly.setHours(0, 0, 0, 0);
        return gameDateOnly < today;
    };

    // Check if there's a game today and trigger celebration
    const hasGameToday = games.some(isGameToday);

    useEffect(() => {
        if (hasGameToday && !loading) {
            setShowCelebration(true);
            // Hide celebration after 4 seconds
            const timer = setTimeout(() => {
                setShowCelebration(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [hasGameToday, loading]);

    // Split games into upcoming and past
    const upcomingGames = games.filter(game => !isGamePast(game));
    const pastGames = games.filter(game => isGamePast(game));

    // Sort upcoming games by date (soonest first)
    upcomingGames.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return a.date.seconds - b.date.seconds;
    });

    // Sort past games by date (most recent first)
    pastGames.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return b.date.seconds - a.date.seconds;
    });

    const myGames = upcomingGames.filter(game =>
        game.players?.includes(currentUser.uid) ||
        game.waitlist?.includes(currentUser.uid)
    );

    const availableGames = upcomingGames.filter(game =>
        !game.players?.includes(currentUser.uid) &&
        !game.waitlist?.includes(currentUser.uid)
    );

    const myPastGames = pastGames.filter(game =>
        game.players?.includes(currentUser.uid) ||
        game.waitlist?.includes(currentUser.uid)
    );

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes highlightPulse {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(0, 112, 8, 0);
                    }
                    50% {
                        box-shadow: 0 0 20px 8px rgba(0, 112, 8, 0.6);
                    }
                }

                @keyframes fadeBackground {
                    0% {
                        background-color: #ffffff;
                    }
                    20% {
                        background-color: #e8f5e9;
                    }
                    100% {
                        background-color: #ffffff;
                    }
                }

                @keyframes confettiFall {
                    0% {
                        transform: translateY(-100vh) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100vh) rotate(720deg);
                        opacity: 0;
                    }
                }

                @keyframes celebrationPulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.9;
                    }
                }

                @keyframes todayCardGlow {
                    0%, 100% {
                        box-shadow: 0 0 20px 5px rgba(255, 215, 0, 0.4), 0 8px 30px rgba(0,0,0,0.6), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #ffd966;
                    }
                    50% {
                        box-shadow: 0 0 35px 10px rgba(255, 215, 0, 0.6), 0 8px 30px rgba(0,0,0,0.6), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #ffd966;
                    }
                }

                @keyframes todayBadgePulse {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.05);
                    }
                }

                .today-game-card {
                    animation: todayCardGlow 2s ease-in-out infinite;
                    border: 3px solid #ffd700 !important;
                }

                .today-badge {
                    animation: todayBadgePulse 1.5s ease-in-out infinite;
                }

                .confetti {
                    position: fixed;
                    width: 10px;
                    height: 10px;
                    top: -10px;
                    animation: confettiFall 3s linear forwards;
                    z-index: 9999;
                    pointer-events: none;
                }

                @media (max-width: 768px) {
                    .header-title-full {
                        display: none !important;
                    }
                    .header-title-short {
                        display: inline !important;
                    }
                    .action-buttons {
                        justify-content: center !important;
                    }
                }
                @media (min-width: 769px) {
                    .header-title-full {
                        display: inline !important;
                    }
                    .header-title-short {
                        display: none !important;
                    }
                }
            `}</style>

            {/* Celebration overlay */}
            {showCelebration && (
                <>
                    {/* Confetti pieces */}
                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="confetti"
                            style={{
                                left: `${Math.random() * 100}%`,
                                backgroundColor: ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ff69b4', '#ffd93d'][Math.floor(Math.random() * 7)],
                                animationDelay: `${Math.random() * 2}s`,
                                animationDuration: `${2 + Math.random() * 2}s`,
                                borderRadius: Math.random() > 0.5 ? '50%' : '0',
                                width: `${8 + Math.random() * 8}px`,
                                height: `${8 + Math.random() * 8}px`,
                            }}
                        />
                    ))}
                    {/* Celebration banner */}
                    <div style={styles.celebrationBanner}>
                        <span style={styles.celebrationEmoji}>🎉</span>
                        <span style={styles.celebrationText}>יש משחק היום!</span>
                        <span style={styles.celebrationEmoji}>🎰</span>
                    </div>
                </>
            )}
            <div style={styles.header}>
                <h1 style={styles.headerTitle}>
                    <span className="header-title-full" style={styles.headerTitleFull}>♠️♥️ פוקר - הרשמה למשחקים ♦️♣️</span>
                    <span className="header-title-short" style={styles.headerTitleShort}>♠️♥️ פוקר ♦️♣️</span>
                </h1>

                {/* Hamburger Menu Button */}
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    style={styles.hamburgerButton}
                    aria-label="תפריט"
                >
                    <div style={styles.hamburgerIcon}>
                        <span style={{...styles.hamburgerLine, transform: menuOpen ? 'rotate(45deg) translateY(8px)' : 'none'}}></span>
                        <span style={{...styles.hamburgerLine, opacity: menuOpen ? 0 : 1}}></span>
                        <span style={{...styles.hamburgerLine, transform: menuOpen ? 'rotate(-45deg) translateY(-8px)' : 'none'}}></span>
                    </div>
                </button>

                {/* Dropdown Menu */}
                {menuOpen && (
                    <div style={styles.dropdownMenu}>
                        <div style={styles.userGreeting}>
                            שלום, {userProfile?.displayName}!
                        </div>
                        <button
                            onClick={() => {
                                navigate('/profile');
                                setMenuOpen(false);
                            }}
                            style={styles.menuItem}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            ⚙️ פרופיל
                        </button>
                        <button
                            onClick={() => {
                                handleLogout();
                                setMenuOpen(false);
                            }}
                            style={styles.menuItem}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ffe5e5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                            🚪 התנתק
                        </button>
                    </div>
                )}
            </div>

            <div style={styles.content}>
                <div className="action-buttons" style={styles.actionButtons}>
                    {userProfile?.isAdmin && (
                        <button
                            onClick={() => navigate('/admin')}
                            style={styles.adminButton}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.5)';
                                e.target.style.backgroundColor = '#f4c842';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                e.target.style.backgroundColor = '#ffd966';
                            }}
                        >
                            👨‍💼 ניהול מערכת
                        </button>
                    )}
                    <button
                        onClick={() => setShowHostRequest(true)}
                        style={styles.hostButton}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.5)';
                            e.target.style.backgroundColor = '#005c06';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                            e.target.style.backgroundColor = '#007008';
                        }}
                    >
                        🏠 יכול לארח?
                    </button>
                    <button
                        onClick={() => window.open('https://links.payboxapp.com/m8JMCZ4pNUb', '_blank')}
                        style={styles.payboxButton}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.5)';
                            e.target.style.backgroundColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                            e.target.style.backgroundColor = '#3b82f6';
                        }}
                    >
                        💳 קישור לפייבוקס
                    </button>
                </div>

                {showHostRequest && (
                    <HostRequestForm
                        onClose={() => {
                            setShowHostRequest(false);
                            setEditingRequest(null);
                        }}
                        onSuccess={() => {
                            setShowHostRequest(false);
                            setEditingRequest(null);
                            loadMyHostRequests();
                            alert('הבקשה נשלחה! מנהל המערכת יבדוק אותה בקרוב.');
                        }}
                        editingRequest={editingRequest}
                        games={games}
                    />
                )}

                {/* My Host Requests */}
                {myHostRequests.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>🏠 בקשות האירוח שלי</h2>
                        <div style={styles.hostRequestsList}>
                            {myHostRequests.map(request => (
                                <div key={request.id} style={styles.hostRequestCard}>
                                    <div style={styles.hostRequestHeader}>
                                        <h3 style={styles.hostRequestTitle}>בקשת אירוח</h3>
                                        <span style={styles.pendingBadge}>⏳ ממתין לאישור</span>
                                    </div>

                                    <div style={styles.hostRequestInfo}>
                                        <p style={styles.hostRequestDate}>
                                            📅 {request.requestedDate?.toDate().toLocaleDateString('he-IL', {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>

                                        {request.message && (
                                            <p style={styles.hostRequestMessage}>
                                                💬 {request.message}
                                            </p>
                                        )}

                                        <p style={styles.hostRequestCreated}>
                                            נשלח ב-{request.createdAt?.toDate().toLocaleDateString('he-IL', {
                                                day: 'numeric',
                                                month: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>

                                    <div style={styles.hostRequestActions}>
                                        <button
                                            onClick={() => {
                                                setEditingRequest(request);
                                                setShowHostRequest(true);
                                            }}
                                            style={styles.editButton}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#0056b3';
                                                e.target.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#007bff';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            ✏️ ערוך
                                        </button>
                                        <button
                                            onClick={() => handleDeleteHostRequest(request.id)}
                                            style={styles.deleteButton}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#a00000';
                                                e.target.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#cc0000';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            🗑️ מחק
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* My Games */}
                {myGames.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>המשחקים שלי</h2>
                        <div style={styles.gamesList}>
                            {myGames.map(game => (
                                <GameCard
                                    key={game.id}
                                    game={game}
                                    userId={currentUser.uid}
                                    onClick={() => navigate(`/game/${game.id}`)}
                                    isToday={isGameToday(game)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Available Games */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        {myGames.length > 0 ? 'משחקים זמינים נוספים' : 'משחקים זמינים'}
                    </h2>

                    {loading ? (
                        <p>טוען משחקים...</p>
                    ) : availableGames.length === 0 ? (
                        <p style={styles.emptyText}>
                            אין משחקים זמינים כרגע.
                            {userProfile?.isAdmin && ' צור משחק חדש מניהול המערכת!'}
                        </p>
                    ) : (
                        <div style={styles.gamesList}>
                            {availableGames.map(game => (
                                <GameCard
                                    key={game.id}
                                    game={game}
                                    userId={currentUser.uid}
                                    onClick={() => navigate(`/game/${game.id}`)}
                                    isToday={isGameToday(game)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Past Games */}
                {myPastGames.length > 0 && (
                    <div style={styles.section}>
                        <h2 style={styles.pastSectionTitle}>📜 משחקים קודמים</h2>
                        <div style={styles.pastGamesList}>
                            {myPastGames.map(game => (
                                <PastGameCard
                                    key={game.id}
                                    game={game}
                                    userId={currentUser.uid}
                                    onClick={() => navigate(`/game/${game.id}`)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function HostRequestForm({ onClose, onSuccess, editingRequest, games }) {
    const { currentUser } = useAuth();
    const [date, setDate] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [dateHighlight, setDateHighlight] = useState(false);

    // Function to get next 4 Thursdays that have no hosted games
    function getAvailableThursdays() {
        const availableThursdays = [];
        const today = new Date();
        let currentDate = new Date(today);

        // Move to next Thursday
        const daysUntilThursday = (4 - currentDate.getDay() + 7) % 7;
        if (daysUntilThursday === 0 && currentDate.getHours() >= 21) {
            // If it's Thursday but after 21:00, start from next week
            currentDate.setDate(currentDate.getDate() + 7);
        } else if (daysUntilThursday > 0) {
            currentDate.setDate(currentDate.getDate() + daysUntilThursday);
        }

        // Get games dates normalized to just the date (remove time)
        const gameThursdayDates = games
            .filter(game => game.date)
            .map(game => {
                const gameDate = new Date(game.date.seconds * 1000);
                return new Date(gameDate.getFullYear(), gameDate.getMonth(), gameDate.getDate()).getTime();
            });

        // Keep checking Thursdays until we have 4 available ones (check up to 12 weeks)
        let weeksChecked = 0;
        while (availableThursdays.length < 4 && weeksChecked < 12) {
            const thursday = new Date(currentDate);
            thursday.setHours(21, 0, 0, 0);

            const thursdayDateOnly = new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate()).getTime();

            // Only add if this Thursday doesn't have a game
            if (!gameThursdayDates.includes(thursdayDateOnly)) {
                availableThursdays.push(thursday);
            }

            currentDate.setDate(currentDate.getDate() + 7);
            weeksChecked++;
        }

        return availableThursdays;
    }

    const availableThursdays = getAvailableThursdays();

    // Function to handle clicking a suggested Thursday
    function handleThursdaySuggestion(thursday) {
        const year = thursday.getFullYear();
        const month = String(thursday.getMonth() + 1).padStart(2, '0');
        const day = String(thursday.getDate()).padStart(2, '0');
        const hours = String(thursday.getHours()).padStart(2, '0');
        const minutes = String(thursday.getMinutes()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}T${hours}:${minutes}`;
        setDate(dateString);

        // Trigger highlight animation
        setDateHighlight(true);

        // Scroll to date input smoothly
        setTimeout(() => {
            const dateInput = document.querySelector('input[type="datetime-local"]');
            if (dateInput) {
                dateInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                dateInput.focus();
            }
        }, 100);

        // Remove highlight after animation
        setTimeout(() => {
            setDateHighlight(false);
        }, 2000);
    }

    // Set default date to first available Thursday or load existing request data
    useEffect(() => {
        if (editingRequest) {
            // Load existing request data
            const requestDate = editingRequest.requestedDate?.toDate();
            if (requestDate) {
                const year = requestDate.getFullYear();
                const month = String(requestDate.getMonth() + 1).padStart(2, '0');
                const day = String(requestDate.getDate()).padStart(2, '0');
                const hours = String(requestDate.getHours()).padStart(2, '0');
                const minutes = String(requestDate.getMinutes()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}T${hours}:${minutes}`;
                setDate(dateString);
            }
            setMessage(editingRequest.message || '');
        } else {
            // Set default to first available Thursday from suggestions
            if (availableThursdays.length > 0) {
                const firstThursday = availableThursdays[0];
                const year = firstThursday.getFullYear();
                const month = String(firstThursday.getMonth() + 1).padStart(2, '0');
                const day = String(firstThursday.getDate()).padStart(2, '0');
                const hours = String(firstThursday.getHours()).padStart(2, '0');
                const minutes = String(firstThursday.getMinutes()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}T${hours}:${minutes}`;
                setDate(dateString);
            } else {
                // Fallback to today at 21:00 if no Thursdays available
                const today = new Date();
                today.setHours(21, 0, 0, 0);
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                const hours = String(today.getHours()).padStart(2, '0');
                const minutes = String(today.getMinutes()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}T${hours}:${minutes}`;
                setDate(dateString);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingRequest]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (!date) {
            setError('אנא בחר תאריך');
            return;
        }

        try {
            setSubmitting(true);

            if (editingRequest) {
                // Update existing request
                await updateDoc(doc(db, 'hostRequests', editingRequest.id), {
                    requestedDate: new Date(date),
                    message: message.trim(),
                    updatedAt: new Date()
                });
            } else {
                // Create new request
                await addDoc(collection(db, 'hostRequests'), {
                    userId: currentUser.uid,
                    requestedDate: new Date(date),
                    message: message.trim(),
                    status: 'pending',
                    createdAt: new Date()
                });
            }

            onSuccess();
        } catch (err) {
            setError('שגיאה בשליחת הבקשה: ' + err.message);
            setSubmitting(false);
        }
    }

    return (
        <div style={styles.modal}>
            <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                    <h2>{editingRequest ? '✏️ ערוך בקשת אירוח' : '🏠 יכול לארח?'}</h2>
                    <button
                        onClick={onClose}
                        style={styles.closeButton}
                        onMouseEnter={(e) => e.target.style.color = '#cc0000'}
                        onMouseLeave={(e) => e.target.style.color = '#666666'}
                    >✕</button>
                </div>

                {error && <div style={styles.error}>{error}</div>}

                {/* Thursday Suggestions */}
                {availableThursdays.length > 0 && (
                    <div style={styles.thursdaySuggestions}>
                        <h3 style={styles.suggestionsTitle}>📅 ימי חמישי זמינים</h3>
                        <div style={styles.suggestionsList}>
                            {availableThursdays.map((thursday, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleThursdaySuggestion(thursday)}
                                    style={styles.suggestionButton}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#007008';
                                        e.currentTarget.style.color = '#ffffff';
                                        e.currentTarget.style.borderColor = '#005c06';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.4)';
                                        // Change all child elements to white
                                        const children = e.currentTarget.querySelectorAll('*');
                                        children.forEach(child => child.style.color = '#ffffff');
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffffff';
                                        e.currentTarget.style.color = '#007008';
                                        e.currentTarget.style.borderColor = '#007008';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                                        // Restore original colors
                                        const timeSpans = e.currentTarget.querySelectorAll('span');
                                        timeSpans.forEach(span => span.style.color = '#666666');
                                    }}
                                >
                                    {thursday.toLocaleDateString('he-IL', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                    <br />
                                    <span style={styles.suggestionTime}>21:00</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>תאריך ושעה מועדפים *</label>
                        <input
                            type="datetime-local"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            style={{
                                ...styles.input,
                                ...(dateHighlight ? styles.inputHighlight : {})
                            }}
                            required
                            onFocus={(e) => e.target.style.borderColor = '#ffd966'}
                            onBlur={(e) => e.target.style.borderColor = dateHighlight ? '#007008' : '#c0c0c0'}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>הודעה נוספת (אופציונלי)</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="מידע נוסף למנהל..."
                            style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                            dir="rtl"
                            onFocus={(e) => e.target.style.borderColor = '#ffd966'}
                            onBlur={(e) => e.target.style.borderColor = '#c0c0c0'}
                        />
                    </div>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={styles.cancelButton}
                            disabled={submitting}
                            onMouseEnter={(e) => {
                                if (!submitting) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.5)';
                                    e.target.style.backgroundColor = '#909090';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!submitting) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                    e.target.style.backgroundColor = '#c0c0c0';
                                }
                            }}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            style={styles.saveButton}
                            disabled={submitting}
                            onMouseEnter={(e) => {
                                if (!submitting) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.5)';
                                    e.target.style.backgroundColor = '#005c06';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!submitting) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                                    e.target.style.backgroundColor = '#007008';
                                }
                            }}
                        >
                            {submitting ? (editingRequest ? 'מעדכן...' : 'שולח...') : (editingRequest ? 'עדכן בקשה' : 'שלח בקשה')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function GameCard({ game, userId, onClick, isToday }) {
    const isPlayer = game.players?.includes(userId);
    const isWaitlist = game.waitlist?.includes(userId);
    const playerCount = game.players?.length || 0;
    const waitlistCount = game.waitlist?.length || 0;
    const isFull = playerCount >= 10;

    // Check if registration is open (using Israel timezone)
    const now = new Date();
    const registrationOpenTime = game.registrationOpenTime
        ? new Date(game.registrationOpenTime.seconds * 1000)
        : null;
    const isRegistrationOpen = !registrationOpenTime || now >= registrationOpenTime;

    return (
        <div
            style={isToday ? styles.todayGameCard : styles.gameCard}
            className={isToday ? 'today-game-card' : ''}
            onClick={onClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                if (!isToday) {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 215, 0, 0.3)';
                    e.currentTarget.style.borderColor = '#ffd700';
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                if (!isToday) {
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
                    e.currentTarget.style.borderColor = 'rgba(218, 165, 32, 0.3)';
                }
            }}
        >
            {isToday && (
                <div className="today-badge" style={styles.todayBadge}>
                    🎯 היום!
                </div>
            )}
            <h3 style={isToday ? styles.todayGameTitle : styles.gameTitle}>{game.name}</h3>

            {game.date && (
                <p style={styles.gameDate}>
                    📅 {new Date(game.date.seconds * 1000).toLocaleDateString('he-IL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
                </p>
            )}

            {game.address && (
                <p style={styles.gameAddress}>📍 {game.address}</p>
            )}

            {!isRegistrationOpen && registrationOpenTime && (
                <p style={styles.registrationClosed}>
                    🔒 הרשמה תפתח ב-{registrationOpenTime.toLocaleDateString('he-IL', {
                        day: 'numeric',
                        month: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </p>
            )}

            <div style={styles.gameStats}>
        <span style={styles.stat}>
          👥 {playerCount}/10 שחקנים
        </span>
                {waitlistCount > 0 && (
                    <span style={styles.statWaitlist}>
            ⏳ {waitlistCount} ממתינים
          </span>
                )}
            </div>

            {isPlayer && <span style={styles.badge}>✅ רשום</span>}
            {isWaitlist && <span style={styles.badgeWaitlist}>⏳ ברשימת המתנה</span>}
            {!isPlayer && !isWaitlist && isFull && (
                <span style={styles.badgeFull}>מלא</span>
            )}
            {!isPlayer && !isWaitlist && !isFull && !isRegistrationOpen && (
                <span style={styles.badgeLocked}>🔒 נעול</span>
            )}
        </div>
    );
}

function PastGameCard({ game, userId, onClick }) {
    const wasPlayer = game.players?.includes(userId);
    const playerCount = game.players?.length || 0;

    return (
        <div
            style={styles.pastGameCard}
            onClick={onClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
            }}
        >
            <div style={styles.pastGameHeader}>
                <h3 style={styles.pastGameTitle}>{game.name}</h3>
                {wasPlayer && <span style={styles.pastBadge}>✓ השתתפת</span>}
            </div>

            {game.date && (
                <p style={styles.pastGameDate}>
                    📅 {new Date(game.date.seconds * 1000).toLocaleDateString('he-IL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                })}
                </p>
            )}

            <div style={styles.pastGameStats}>
                <span>👥 {playerCount} שחקנים</span>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 50%, #1a1a1a 100%)',
        direction: 'rtl',
    },
    celebrationBanner: {
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 112, 8, 0.95)',
        color: '#ffd700',
        padding: '15px 40px',
        borderRadius: '50px',
        fontSize: '24px',
        fontWeight: 'bold',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        boxShadow: '0 8px 32px rgba(0, 112, 8, 0.5)',
        border: '3px solid #ffd700',
        animation: 'celebrationPulse 1s ease-in-out infinite',
    },
    celebrationEmoji: {
        fontSize: '32px',
    },
    celebrationText: {
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
    },
    header: {
        backgroundColor: '#007008',
        padding: '15px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.8)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '15px',
        borderBottom: '3px solid #ffd966',
        position: 'relative',
        minHeight: '60px',
    },
    headerTitle: {
        color: '#ffd966',
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
        fontSize: 'clamp(16px, 4vw, 28px)',
        margin: '0',
        fontWeight: 'bold',
        textAlign: 'center',
        flex: '1',
    },
    headerTitleFull: {
        display: 'inline',
    },
    headerTitleShort: {
        display: 'none',
    },
    hamburgerButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1001,
    },
    hamburgerIcon: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        width: '30px',
    },
    hamburgerLine: {
        width: '30px',
        height: '3px',
        backgroundColor: '#ffd966',
        borderRadius: '3px',
        transition: 'all 0.3s ease',
        display: 'block',
    },
    dropdownMenu: {
        position: 'absolute',
        top: '100%',
        left: '20px',
        backgroundColor: '#ffffff',
        border: '3px solid #000000',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        minWidth: '200px',
        zIndex: 1000,
        overflow: 'hidden',
        marginTop: '10px',
    },
    userGreeting: {
        padding: '15px 20px',
        backgroundColor: '#f9f9f9',
        color: '#000000',
        fontWeight: 'bold',
        borderBottom: '2px solid #ffd966',
        textAlign: 'right',
        fontSize: '16px',
    },
    menuItem: {
        width: '100%',
        padding: '15px 20px',
        backgroundColor: 'transparent',
        border: 'none',
        textAlign: 'right',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        color: '#000000',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        justifyContent: 'flex-end',
    },
    content: {
        padding: '30px 20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    actionButtons: {
        display: 'flex',
        gap: '15px',
        marginBottom: '30px',
        flexWrap: 'wrap',
    },
    baseButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    adminButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#ffd966',
        color: '#000000',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    hostButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#007008',
        color: '#ffffff',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    payboxButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    section: {
        marginBottom: '40px',
    },
    sectionTitle: {
        fontSize: '24px',
        color: '#ffd966',
        marginBottom: '20px',
        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
        fontWeight: 'bold',
    },
    pastSectionTitle: {
        fontSize: '20px',
        color: '#9ca3af',
        marginBottom: '15px',
        textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
        fontWeight: 'bold',
        borderTop: '1px solid rgba(156, 163, 175, 0.3)',
        paddingTop: '30px',
    },
    gamesList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px',
    },
    pastGamesList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '15px',
    },
    pastGameCard: {
        backgroundColor: 'rgba(40, 40, 40, 0.8)',
        padding: '18px',
        borderRadius: '10px',
        border: '1px solid rgba(156, 163, 175, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
    },
    pastGameHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '10px',
        marginBottom: '8px',
    },
    pastGameTitle: {
        margin: '0',
        color: '#d1d5db',
        fontSize: '16px',
        fontWeight: '600',
    },
    pastGameDate: {
        color: '#9ca3af',
        fontSize: '13px',
        margin: '6px 0',
    },
    pastGameStats: {
        color: '#6b7280',
        fontSize: '12px',
        marginTop: '10px',
        paddingTop: '10px',
        borderTop: '1px solid rgba(156, 163, 175, 0.2)',
    },
    pastBadge: {
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        color: '#22c55e',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
    },
    gameCard: {
        backgroundColor: '#ffffff',
        padding: '25px',
        borderRadius: '15px',
        border: '3px solid #000000',
        boxShadow: '0 8px 30px rgba(0,0,0,0.6), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #ffd966',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
    },
    todayGameCard: {
        backgroundColor: '#fffef0',
        padding: '25px',
        borderRadius: '15px',
        border: '3px solid #ffd700',
        boxShadow: '0 0 20px 5px rgba(255, 215, 0, 0.4), 0 8px 30px rgba(0,0,0,0.6), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #ffd966',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        background: 'linear-gradient(145deg, #fffef0 0%, #fff8dc 100%)',
    },
    todayBadge: {
        position: 'absolute',
        top: '-12px',
        right: '20px',
        backgroundColor: '#ff6b35',
        color: '#ffffff',
        padding: '6px 16px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: 'bold',
        boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
        border: '2px solid #ffffff',
        zIndex: 10,
    },
    todayGameTitle: {
        margin: '0 0 10px 0',
        color: '#007008',
        fontSize: '22px',
        fontWeight: 'bold',
    },
    gameTitle: {
        margin: '0 0 10px 0',
        color: '#000000',
        fontSize: '20px',
        fontWeight: 'bold',
    },
    gameDate: {
        color: '#333333',
        fontSize: '14px',
        margin: '8px 0',
    },
    gameAddress: {
        color: '#666666',
        fontSize: '13px',
        margin: '8px 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    gameStats: {
        display: 'flex',
        gap: '15px',
        marginTop: '15px',
        paddingTop: '15px',
        borderTop: '2px solid #ffd966',
    },
    stat: {
        color: '#007008',
        fontWeight: 'bold',
        fontSize: '14px',
    },
    statWaitlist: {
        color: '#cc6600',
        fontWeight: 'bold',
        fontSize: '14px',
    },
    badge: {
        position: 'absolute',
        top: '15px',
        left: '15px',
        background: 'radial-gradient(circle at 30% 30%, #007008, #005006 40%, #007008)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 3px 10px rgba(0,0,0,0.5), inset 0 0 0 1px #ffffff, inset 0 0 0 2px #007008',
        border: '2px solid #000000',
    },
    badgeWaitlist: {
        position: 'absolute',
        top: '15px',
        left: '15px',
        background: 'radial-gradient(circle at 30% 30%, #cc6600, #994c00 40%, #cc6600)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 3px 10px rgba(0,0,0,0.5), inset 0 0 0 1px #ffffff, inset 0 0 0 2px #cc6600',
        border: '2px solid #000000',
    },
    badgeFull: {
        position: 'absolute',
        top: '15px',
        left: '15px',
        background: 'radial-gradient(circle at 30% 30%, #c0c0c0, #909090 40%, #c0c0c0)',
        color: '#000000',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 3px 10px rgba(0,0,0,0.5), inset 0 0 0 1px #ffffff, inset 0 0 0 2px #c0c0c0',
        border: '2px solid #000000',
    },
    badgeLocked: {
        position: 'absolute',
        top: '15px',
        left: '15px',
        background: 'radial-gradient(circle at 30% 30%, #cc0000, #990000 40%, #cc0000)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 3px 10px rgba(0,0,0,0.5), inset 0 0 0 1px #ffffff, inset 0 0 0 2px #cc0000',
        border: '2px solid #000000',
    },
    registrationClosed: {
        color: '#cc6600',
        fontSize: '13px',
        marginTop: '8px',
        fontWeight: '600',
    },
    emptyText: {
        color: '#666666',
        fontStyle: 'italic',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#ffffff',
        borderRadius: '15px',
        border: '3px solid #000000',
        boxShadow: '0 8px 30px rgba(0,0,0,0.5), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #ffd966',
    },
    modal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '15px',
        padding: '35px',
        maxWidth: '500px',
        width: '100%',
        direction: 'rtl',
        border: '3px solid #000000',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.9), inset 0 0 0 8px #ffffff, inset 0 0 0 12px #ffd966',
        background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        color: '#000000',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '28px',
        cursor: 'pointer',
        color: '#666666',
        transition: 'color 0.3s ease',
        padding: '0',
        lineHeight: '1',
    },
    error: {
        backgroundColor: '#ffe5e5',
        color: '#cc0000',
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '2px solid #cc0000',
        textAlign: 'center',
    },
    formGroup: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#000000',
        fontSize: '15px',
    },
    input: {
        width: '100%',
        padding: '14px 16px',
        border: '2px solid #c0c0c0',
        borderRadius: '8px',
        fontSize: '16px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        color: '#000000',
        transition: 'all 0.3s ease',
        outline: 'none',
        colorScheme: 'light',
    },
    inputHighlight: {
        borderColor: '#007008',
        borderWidth: '3px',
        animation: 'highlightPulse 2s ease-in-out, fadeBackground 2s ease-in-out',
    },
    modalActions: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end',
        marginTop: '30px',
    },
    cancelButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#c0c0c0',
        color: '#000000',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    saveButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40px',
        borderRadius: '80px',
        fontSize: '16px',
        lineHeight: '20px',
        letterSpacing: '0.64px',
        fontWeight: '500',
        paddingTop: '9px',
        paddingRight: '24px',
        paddingBottom: '9px',
        paddingLeft: '24px',
        width: '220px',
        transition: 'background-color 0.25s ease-in-out, border-color 0.24s ease-in-out, transform 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: '#007008',
        color: '#ffffff',
        border: '3px solid #000000',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    },
    hostRequestsList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px',
    },
    hostRequestCard: {
        backgroundColor: '#ffffff',
        padding: '25px',
        borderRadius: '15px',
        border: '3px solid #000000',
        boxShadow: '0 8px 30px rgba(0,0,0,0.6), inset 0 0 0 6px #ffffff, inset 0 0 0 10px #007008',
        background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
    },
    hostRequestHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        paddingBottom: '15px',
        borderBottom: '2px solid #007008',
    },
    hostRequestTitle: {
        margin: 0,
        color: '#000000',
        fontSize: '18px',
        fontWeight: 'bold',
    },
    pendingBadge: {
        background: 'radial-gradient(circle at 30% 30%, #cc6600, #994c00 40%, #cc6600)',
        color: 'white',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        border: '2px solid #000000',
    },
    hostRequestInfo: {
        marginBottom: '20px',
    },
    hostRequestDate: {
        color: '#000000',
        fontSize: '15px',
        margin: '8px 0',
        fontWeight: '600',
    },
    hostRequestMessage: {
        color: '#333333',
        fontSize: '14px',
        margin: '12px 0',
        padding: '12px',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        fontStyle: 'italic',
    },
    hostRequestCreated: {
        color: '#666666',
        fontSize: '12px',
        margin: '8px 0',
    },
    hostRequestActions: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end',
        paddingTop: '15px',
        borderTop: '2px solid #e0e0e0',
    },
    editButton: {
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: '#ffffff',
        border: '2px solid #000000',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
    deleteButton: {
        padding: '10px 20px',
        backgroundColor: '#cc0000',
        color: '#ffffff',
        border: '2px solid #000000',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
    thursdaySuggestions: {
        marginBottom: '25px',
        padding: '20px',
        backgroundColor: '#f9f9f9',
        borderRadius: '12px',
        border: '2px solid #007008',
    },
    suggestionsTitle: {
        margin: '0 0 15px 0',
        color: '#007008',
        fontSize: '16px',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    suggestionsList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
    },
    suggestionButton: {
        padding: '12px',
        backgroundColor: '#ffffff',
        color: '#007008',
        border: '2px solid #007008',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        textAlign: 'center',
        lineHeight: '1.4',
    },
    suggestionTime: {
        display: 'block',
        fontSize: '12px',
        color: '#666666',
        marginTop: '4px',
    },
};
